import React, { useRef, useEffect, forwardRef, useState } from 'react';
import PromptHireLogo from '../../../assets/prompthire_logo.png';
import useDailyCall from '../hooks/useDailyCall';
import { AlertCircle } from 'lucide-react';
import useFaceDetection from '../../../hooks/useFaceDetection';
import { useWebSocket } from '../../../WebSocketContext';
import logger from './../../../utils/logger.js';


// Using the shared face detection hook from src/hooks/useFaceDetection.js

// Alert component for face detection notifications
const FaceAlert = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="absolute top-0 left-0 right-0 bg-red-600 text-white py-4 px-6 flex items-center justify-center gap-3 z-40 shadow-lg">
      <AlertCircle className="h-6 w-6 text-white animate-pulse" />
      <span className="text-white font-bold text-lg">{message}</span>
      <AlertCircle className="h-6 w-6 text-white animate-pulse" />
    </div>
  );
};

// Face count indicator
const FaceCountIndicator = ({ count }) => {
  const bgColor = count > 1 ? 'bg-red-500' : 'bg-green-500';
  
  return (
    <></>
    // <div className={`absolute top-16 left-4 ${bgColor} text-white px-4 py-2 rounded-md z-20 font-medium shadow-md`}>
    //   <div className="flex items-center gap-2">
    //     <AlertCircle className="h-5 w-5" />
    //     <span>Faces: {count}</span>
    //   </div>
    // </div>
  );
};

const VideoDisplay = forwardRef(({ videoRef, isMobile, userName, isSpeaking, evaSpeaking, roomUrl, question, isMCQComponentVisible, currentQuestionIndex, totalQuestions, isProctoringJoined }, ref) => {
  const dailyCallRef = useRef(null);
  
  // Use shared face detection hook with enhanced alerts
  const { faceCount, faceDetectionStatus } = useFaceDetection(videoRef, null);
  
  // Get WebSocket context to use face detection events
  const { sendNoFaceDetectedEvent, sendMultipleFacesDetectedEvent } = useWebSocket();
  
  // Track previous detection status to avoid duplicate events
  const prevStatusRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Send face detection events when status changes
  useEffect(() => {
    // Only send events if we have a valid faceDetectionStatus and it changed
    if (faceDetectionStatus && faceDetectionStatus !== prevStatusRef.current) {
      logger.debug('Face detection status changed:', faceDetectionStatus);
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Debounce the status change event (500ms)
      timeoutRef.current = setTimeout(() => {
        // Only send events for no_face and multiple_faces, not for single face
        if (faceDetectionStatus === 'no_face') {
          sendNoFaceDetectedEvent();
        } else if (faceDetectionStatus === 'multiple_faces') {
          sendMultipleFacesDetectedEvent(faceCount);
        }
        // Don't send any event for single face detection
        
        // Update previous status after sending
        prevStatusRef.current = faceDetectionStatus;
      }, 500);
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [faceDetectionStatus, faceCount, sendNoFaceDetectedEvent, sendMultipleFacesDetectedEvent]);
  
  // Custom status messages for the interview context
  const getCustomStatusMessage = (status) => {
    switch(status) {
      case 'no_face':
        return 'Please stay in frame';
      case 'multiple_faces':
        return 'Multiple faces detected';
      default:
        return '';
    }
  };
  
  // Enhanced status messages for better user guidance
  const customStatus = getCustomStatusMessage(faceDetectionStatus);
  const enhancedFaceStatus = customStatus === "Please stay in frame" 
    ? "⚠️ WARNING: Please stay in frame for the interview ⚠️" 
    : customStatus === "Multiple faces detected" 
    ? "⚠️ WARNING: Only one person should be visible ⚠️" 
    : customStatus;

  const {
    callFrame,
    participants,
    callState,
    isMuted,
    isVideoOff,
    joinCall,
    leaveCall,
    toggleMute,
    toggleVideo,
    getParticipantTracks,

    
    
  } = useDailyCall();

  // Handle end call event with proper cleanup
  useEffect(() => {
    const handleEndCall = () => {
      logger.debug('end_interview_call event received in VideoDisplay');
      leaveCall();
    };
    
    // Add event listener
    window.addEventListener('end_interview_call', handleEndCall);
    
    // Clean up event listener when component unmounts
    return () => {
      window.removeEventListener('end_interview_call', handleEndCall);
    };
  }, [leaveCall]); // Add leaveCall as a dependency
  
  // Automatically join the call when component mounts
  useEffect(() => {
    if (roomUrl && (callState === 'idle')) {
      logger.debug('Auto-joining call:', roomUrl); 
      joinCall(roomUrl);
    }
  }, [roomUrl, callState, joinCall]);
  
  // If MCQ is visible, we'll keep the component in DOM but make it invisible
  // This preserves the video connection and state
  return (
    <div style={{
      position: 'relative', // Always keep it relative to maintain layout
      width: isMobile ? '100%' : '67vw',
      height: isMobile ? '35vh' : '80vh', // Use exact 30vh for mobile
      marginBottom: isMobile ? '0px' : '0',
      // background: '#f8fafc', // Light background for white theme
      // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', // Subtle shadow for depth
      // borderRadius: isMobile ? '0' : '16px',
      // border: isMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
      visibility: isMCQComponentVisible ? 'hidden' : 'visible', // Use visibility instead of display to keep in DOM
      zIndex: isMCQComponentVisible ? -1 : 100,
      opacity: isMCQComponentVisible ? 0 : 1,
      pointerEvents: isMCQComponentVisible ? 'none' : 'auto',
      padding: isMobile ? '0px' : '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '0px' : '12px'
    }}>
      {/* Enhanced face detection alert with more prominent styling */}
      <FaceAlert message={enhancedFaceStatus} />
      
      {/* Progress Bar */}
      {currentQuestionIndex !== null && totalQuestions !== null && (
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: isMobile ? '4px' : '8px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 4px'
          }}>
            <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '500', color: '#4b5563' }}>
              Question {currentQuestionIndex} of {totalQuestions}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${((currentQuestionIndex) / totalQuestions) * 100}%`,
              backgroundColor: '#3b82f6',
              borderRadius: '3px',
              transition: 'width 0.5s ease-in-out'
            }} />
          </div>
        </div>
      )}
      
      {/* Main content grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gridTemplateRows: isMobile ? '1fr' : 'repeat(2, 1fr)', // Modified to use full space on mobile 
        gap: isMobile ? '0px' : '12px', // Remove gap in mobile view
        flex: 1
      }}>
        {/* Tile 1: Question Display */}
        <div style={{
          gridColumn: isMobile ? '1' : '1 / 3',
          gridRow: isMobile ? '1' : '1 / 2',
          backgroundColor: '#f0f9ff', // Light blue background
          borderRadius: '12px',
          padding: isMobile ? '12px' : '16px',
          color: '#0f172a', // Dark text for contrast
          display: isMobile ? 'none' : 'flex', // Hide on mobile
          flexDirection: 'column',
          justifyContent: 'start',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)', // Lighter shadow for white theme
          border: '1px solid rgba(0, 0, 0, 0.08)', // Subtle border
          position: 'relative',
          overflow: 'hidden',
          minHeight: isMobile ? '80px' : 'auto'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: isMobile ? '4px' : '8px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)', // Subtle border
            paddingBottom: isMobile ? '4px' : '8px'
          }}>
            <div style={{ 
              backgroundColor: '#3b82f6', // Keep the accent color 
              borderRadius: '50%', 
              width: isMobile ? '20px' : '24px',
              height: isMobile ? '20px' : '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '8px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: 'bold',
              color: '#ffffff',
            }}>Q</div>
            <span style={{ fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px', color: '#0f172a' }}>{question ? 'Current Question' : ''}</span>
          </div>
          <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: '500', lineHeight: '1.3' }}>
            {question ? question : 'Your Question will be displayed here'}
          </div>
        </div>
        
        {/* Tile 2: User Video - Hidden on mobile */}
        {!isMobile && (
          <div style={{
            gridColumn: '1 / 2',
            gridRow: '2 / 3',
            backgroundColor: '#ffffff', // White background for tiles
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)', // Lighter shadow for white theme
            border: '1px solid rgba(0, 0, 0, 0.08)', // Subtle border
          }}>
            <video
              ref={videoRef}
              autoPlay={false}
              playsInline
              muted // Always mute local video preview
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'none', // No mirror effect on desktop
              }}
            >
              Your browser does not support the video tag.
            </video>
            
            {/* User name display */}
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              backgroundColor: 'rgba(4, 88, 224, 0.9)', // Blue badge background
              color: '#0f172a', // Dark text for contrast
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span className="text-white">{userName || 'You'}</span>
              {isSpeaking && (
                <div style={{ display: 'flex', gap: '2px' }}>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                </div>
              )}
            </div>
            
            
            {/* Face count indicator - Only show when there's at least one face */}
            {faceCount > 0 && <FaceCountIndicator count={faceCount} />}
            
            {/* Proctoring mode indicator */}
            {isProctoringJoined && (
              <>
              <div className="absolute flex flex-col top-0 right-0 bg-green-500 w-full text-white px-3 z-20 shadow-md flex items-center py-1">
                <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-white animate-pulse"></div>
                <span className="text-xs font-bold">Proctoring Mode On</span>
                </div>
                <p className="text-xs font-medium">Keep second camera on & angled right — else interview may fail.</p>
              </div>
              </>
            )}
          </div>
        )}
        
        {/* Hidden video element for mobile - We keep the video element in the DOM but hide it visually */}
        {isMobile && (
          <div style={{ 
            width: '100%',
            height: '100%',
            backgroundColor: '#ffffff', 
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
          }}>
            <video
              ref={videoRef}
              autoPlay={false}
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'none',
              }}
            >
              Your browser does not support the video tag.
            </video>
            
            {/* User name display */}
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              backgroundColor: 'rgba(4, 88, 224, 0.9)',
              color: '#0f172a',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span className="text-white">{userName || 'You'}</span>
              {isSpeaking && (
                <div style={{ display: 'flex', gap: '2px' }}>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                  <div className={isSpeaking ? "wave" : "disable-wave"} style={{ backgroundColor: '#ffffff' }}></div>
                </div>
              )}
            </div>
            
            {/* Face count indicator - Only show when there's at least one face */}
            {faceCount > 0 && <FaceCountIndicator count={faceCount} />}
            
            {/* Proctoring mode indicator */}
            {isProctoringJoined && (
              <>
              <div className="absolute flex flex-col top-0 right-0 bg-green-500 w-full text-white px-3 z-20 shadow-md flex items-center py-1">
                <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-white animate-pulse"></div>
                <span className="text-xs font-bold">Proctoring Mode On</span>
                </div>
                <p className="text-xs font-medium">Keep second camera on & angled right — else interview may fail.</p>
              </div>
              </>
            )}
          </div>
        )}
        
        {/* Tile 3: Interviewer (Eva) - Hidden on mobile */}
        {!isMobile && (
          <div style={{
            gridColumn: '2 / 3',
            gridRow: '2 / 3',
            backgroundImage: 'linear-gradient(to top, #fbc2eb30 0%, #a6c1ee30 100%)',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)', // Lighter shadow for white theme
            border: '1px solid rgba(0, 0, 0, 0.08)', // Subtle border
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <img
                src={PromptHireLogo}
                alt="eva"
                className={`w-24 h-24 rounded-full border-4 ${
                  evaSpeaking ? 'border-blue-500 animate-flicker' : 'border-gray-500'
                }`}
              />
              {/* <div style={{
                backgroundColor: 'rgba(4, 88, 224, 0.9)', // Blue badge background
                color: '#0f172a', // Dark text for contrast
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {evaSpeaking && (
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <div className="wave" style={{ backgroundColor: '#ffffff' }}></div>
                    <div className="wave" style={{ backgroundColor: '#ffffff' }}></div>
                    <div className="wave" style={{ backgroundColor: '#ffffff' }}></div>
                  </div>
                )}
                <span className="text-white">Interviewer</span>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const styles = {
  aiWaveContainer: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    zIndex: 2
  }
};

export default VideoDisplay;